/**
 * Centralized Permission Rules with Hierarchy
 *
 * This module provides a single source of truth for all permission logic in the frontend,
 * mirroring the backend's hierarchical permission system.
 *
 * Hierarchy:
 * - Superadmins: Full access to everything (including cross-account operations)
 * - Admins: Full access to all features within their account
 * - Specialized Managers: Feature-specific permissions
 * - Regular Users: Basic authenticated access
 *
 * @module permissionRules
 */

/**
 * User group constants
 * Maps to Django groups in the backend
 */
export const GROUPS = {
  SUPERADMIN: 'superadmins',
  ADMIN: 'admins',
  EMERGENCY_MANAGER: 'emergency_substitution_managers',
  ONBOARDING_MANAGER: 'onboarding_managers',
  ENGAGEMENT_MANAGER: 'engagement_managers',
  KNOWLEDGE_BASE_MANAGER: 'knowledge_base_managers',
  CONVERSATION_MANAGER: 'conversation_managers',
};

/**
 * Permission constants with hierarchical groups
 *
 * These define which groups can access each feature.
 * Note: Admins and superadmins are automatically granted access via the hierarchy system.
 */
export const PERMISSIONS = {
  // Cross-account operations (superadmin only)
  CROSS_ACCOUNT_ACCESS: [GROUPS.SUPERADMIN],

  // Administrative operations
  ADMIN_ACCESS: [GROUPS.ADMIN],

  // Feature-specific permissions (managers + admins via hierarchy)
  MANAGE_EMERGENCY: [GROUPS.EMERGENCY_MANAGER],
  MANAGE_ONBOARDING: [GROUPS.ONBOARDING_MANAGER],
  MANAGE_ENGAGEMENT: [GROUPS.ENGAGEMENT_MANAGER],
  MANAGE_KNOWLEDGE: [GROUPS.KNOWLEDGE_BASE_MANAGER],
  MANAGE_CONVERSATIONS: [GROUPS.CONVERSATION_MANAGER],
};

/**
 * Check if user has permission with automatic hierarchy inheritance
 *
 * Hierarchy rules:
 * 1. Superadmins have ALL permissions
 * 2. Admins have all feature permissions (except CROSS_ACCOUNT_ACCESS)
 * 3. Specialized managers have feature-specific permissions
 *
 * @param {Array<{name: string}>} userGroups - User's groups from auth state
 * @param {Array<string>} requiredPermission - Required permission constant (e.g., PERMISSIONS.MANAGE_EMERGENCY)
 * @returns {boolean} True if user has permission
 *
 * @example
 * const canManage = hasPermission(user.groups, PERMISSIONS.MANAGE_EMERGENCY);
 * // Returns true for: superadmins, admins, emergency_substitution_managers
 */
export function hasPermission(userGroups, requiredPermission) {
  if (!userGroups || !Array.isArray(userGroups)) return false;
  if (!requiredPermission || !Array.isArray(requiredPermission)) return false;

  const userGroupNames = userGroups.map(g => g.name);

  // Superadmins have all permissions
  if (userGroupNames.includes(GROUPS.SUPERADMIN)) {
    return true;
  }

  // Admins have all feature permissions (but not cross-account)
  if (requiredPermission !== PERMISSIONS.CROSS_ACCOUNT_ACCESS) {
    if (userGroupNames.includes(GROUPS.ADMIN)) {
      return true;
    }
  }

  // Check if user has any of the required groups
  return requiredPermission.some(group => userGroupNames.includes(group));
}

/**
 * Check if user has a specific group
 *
 * @param {Array<{name: string}>} userGroups - User's groups
 * @param {string} groupName - Group name to check
 * @returns {boolean} True if user has the group
 *
 * @example
 * const isSuperadmin = hasGroup(user.groups, GROUPS.SUPERADMIN);
 */
export function hasGroup(userGroups, groupName) {
  if (!userGroups || !Array.isArray(userGroups)) return false;
  if (!groupName) return false;

  return userGroups.some(g => g.name === groupName);
}

/**
 * Check if user has any of the specified groups
 *
 * @param {Array<{name: string}>} userGroups - User's groups
 * @param {Array<string>} groupNames - Array of group names to check
 * @returns {boolean} True if user has any of the groups
 *
 * @example
 * const isManager = hasAnyGroup(user.groups, [
 *   GROUPS.EMERGENCY_MANAGER,
 *   GROUPS.ONBOARDING_MANAGER
 * ]);
 */
export function hasAnyGroup(userGroups, groupNames) {
  if (!userGroups || !Array.isArray(userGroups)) return false;
  if (!groupNames || !Array.isArray(groupNames)) return false;

  return groupNames.some(name => userGroups.some(g => g.name === name));
}
